import React, { useEffect, useState } from 'react';
import { Loader2, Plus, Trash2, X } from 'lucide-react';
import {
  Button,
  Checkbox,
  DatePicker,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import { MEDICATION_CLASSIFICATIONS } from '../../constants/medications';
import { getDosageUnit } from './medicationUtils';
import { formatDateToISO, parseISOToLocalDate } from '../../utils/dateUtils';
import CandidateScreenAlert from './CandidateScreenAlert';
import MedicationSearch from './MedicationSearch';
import { useToast } from '../../hooks/useToastContext';
import {
  createPatientMedication,
  updatePatientMedication,
} from '../../services/api';
import type {
  FdbMedicationResult,
  MedicationResponse,
  PatientMedicationCreateWeb,
  PatientMedicationResponse,
  PatientMedicationUpdate,
} from '../../services/api';

// A selected drug is either a fresh FDB search result (materialize-on-select via
// fdb_dispensable_drug_id) or an already-linked local medication when editing
// (identified by its local id).
type SelectedMedication = FdbMedicationResult | MedicationResponse;

export type MedicationFormMode = 'standard' | 'nonstandard' | 'offmarket';

interface MedicationFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId: string;
  /** Medication being edited; null when adding a new one. */
  editingMedication: PatientMedicationResponse | null;
  /** Initial type tab when adding (ignored when editing). */
  initialMode?: MedicationFormMode;
  /** Called after a successful create/update so the caller can refresh. */
  onSaved: () => void;
}

/**
 * Add / edit dialog for a patient profile medication. Owns all of its own form
 * state; the parent only controls visibility and which medication is in scope.
 */
const MedicationFormDialog: React.FC<MedicationFormDialogProps> = ({
  open,
  onOpenChange,
  patientId,
  editingMedication,
  initialMode = 'standard',
  onSaved,
}) => {
  const [selectedMedication, setSelectedMedication] =
    useState<SelectedMedication | null>(null);
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [amount, setAmount] = useState<string>('');
  const [discontinueDate, setDiscontinueDate] = useState<Date | undefined>(
    undefined
  );
  const [frequencyInstructions, setFrequencyInstructions] =
    useState<string>('');
  const [isLongstanding, setIsLongstanding] = useState(false);
  const [isChange, setIsChange] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const [isNonstandardDosage, setIsNonstandardDosage] = useState(false);
  const [nonstandardDoses, setNonstandardDoses] = useState<string[]>(['']);
  const [isOffMarketMode, setIsOffMarketMode] = useState(false);
  const [offMarketDescription, setOffMarketDescription] = useState('');
  const [offMarketClassification, setOffMarketClassification] = useState('');
  const [isMedicationChange, setIsMedicationChange] = useState(false);
  const [changeDate, setChangeDate] = useState<Date | undefined>(undefined);
  const [isSaving, setIsSaving] = useState(false);
  const { error: showError } = useToast();

  // Initialize the form whenever the dialog opens, from the medication being
  // edited or from the chosen "add" mode.
  useEffect(() => {
    if (!open) return;

    if (editingMedication) {
      const med = editingMedication;
      setSelectedMedication(med.medication ?? null);
      setStartDate(parseISOToLocalDate(med.start_date));
      setDiscontinueDate(
        med.discontinue_date
          ? parseISOToLocalDate(med.discontinue_date)
          : undefined
      );
      setAmount(med.amount != null ? String(med.amount) : '');
      setFrequencyInstructions(med.frequency_instructions ?? '');
      setIsLongstanding(med.is_longstanding ?? false);
      setIsChange(med.is_change ?? false);
      setIsNew(med.is_new ?? false);
      setIsNonstandardDosage(med.is_nonstandard_dosage ?? false);
      setNonstandardDoses(
        med.nonstandard_doses && med.nonstandard_doses.length > 0
          ? med.nonstandard_doses
          : ['']
      );
      setIsOffMarketMode(med.is_off_market ?? false);
      setOffMarketDescription(med.off_market_description ?? '');
      setOffMarketClassification(med.off_market_classification ?? '');
      setIsMedicationChange(true); // Default to Medication Change mode
      setChangeDate(undefined);
    } else {
      setSelectedMedication(null);
      setStartDate(undefined);
      setAmount('');
      setDiscontinueDate(undefined);
      setFrequencyInstructions('');
      setIsLongstanding(false);
      setIsChange(false);
      setIsNew(false);
      setIsNonstandardDosage(initialMode === 'nonstandard');
      setNonstandardDoses(['']);
      setIsOffMarketMode(initialMode === 'offmarket');
      setOffMarketDescription('');
      setOffMarketClassification('');
      setIsMedicationChange(false);
      setChangeDate(undefined);
    }
    // Only re-init when the dialog opens or its subject changes.
  }, [open, editingMedication, initialMode]);

  const handleClose = (nextOpen: boolean) => {
    if (!nextOpen && !isSaving) {
      onOpenChange(false);
    }
  };

  const handleAddOffMarket = (searchTerm: string) => {
    setIsOffMarketMode(true);
    setOffMarketDescription(searchTerm);
  };

  const addDoseRow = () => setNonstandardDoses([...nonstandardDoses, '']);
  const removeDoseRow = (index: number) =>
    setNonstandardDoses(nonstandardDoses.filter((_, i) => i !== index));
  const updateDoseRow = (index: number, value: string) => {
    const updated = [...nonstandardDoses];
    updated[index] = value;
    setNonstandardDoses(updated);
  };

  /** Shared medication detail payload used by create + update. */
  const buildDetails = () => ({
    amount: isNonstandardDosage ? null : amount ? parseFloat(amount) : null,
    frequency_instructions: frequencyInstructions || null,
    is_nonstandard_dosage: isNonstandardDosage,
    nonstandard_doses: isNonstandardDosage
      ? nonstandardDoses.filter(d => d.trim())
      : null,
    is_longstanding: isLongstanding,
    is_new: isNew,
    is_off_market: isOffMarketMode,
    off_market_description: isOffMarketMode ? offMarketDescription : null,
    off_market_classification: isOffMarketMode
      ? offMarketClassification || null
      : null,
  });

  // Drug-link fields for CREATE bodies only (never sent on an in-place update,
  // so an edit can't accidentally re-link/null the drug). A new FDB selection
  // materializes via its fdb id; an already-linked medication keeps its id.
  const buildDrugLink = (): {
    medication_id: string | null;
    fdb_dispensable_drug_id: string | null;
  } => {
    if (isOffMarketMode || !selectedMedication) {
      return { medication_id: null, fdb_dispensable_drug_id: null };
    }
    if ('id' in selectedMedication) {
      return {
        medication_id: selectedMedication.id,
        fdb_dispensable_drug_id: null,
      };
    }
    return {
      medication_id: null,
      fdb_dispensable_drug_id: selectedMedication.fdb_dispensable_drug_id,
    };
  };

  const handleSave = async () => {
    if (!startDate) return;
    if (editingMedication && isMedicationChange && !changeDate) return;

    setIsSaving(true);
    try {
      const details = buildDetails();

      // The client is configured without `throwOnError`, so HTTP failures come
      // back as a populated `error` rather than a thrown exception. Inspect each
      // response and surface a toast (keeping the dialog open) on failure.
      if (editingMedication && isMedicationChange && changeDate) {
        // Medication change: discontinue the old med, create a new one.
        const updateResponse = await updatePatientMedication({
          path: { patient_id: patientId, medication_id: editingMedication.id },
          body: { discontinue_date: formatDateToISO(changeDate) },
        });
        if (updateResponse.error) {
          showError('Failed to save medication. Please try again.');
          return;
        }
        const createData: PatientMedicationCreateWeb = {
          ...details,
          ...buildDrugLink(),
          start_date: formatDateToISO(changeDate),
          discontinue_date: discontinueDate
            ? formatDateToISO(discontinueDate)
            : null,
          is_change: true,
        };
        const createResponse = await createPatientMedication({
          path: { patient_id: patientId },
          body: createData,
        });
        if (createResponse.error) {
          showError('Failed to save medication. Please try again.');
          return;
        }
      } else if (editingMedication) {
        // Error correction: update in place. `details` intentionally carries no
        // drug-link fields, so this can never re-link/null the medication (drug
        // changes go through the "Medication Change" discontinue+create flow).
        const updateData: PatientMedicationUpdate = {
          ...details,
          start_date: formatDateToISO(startDate),
          discontinue_date: discontinueDate
            ? formatDateToISO(discontinueDate)
            : null,
          is_change: isChange,
        };
        const response = await updatePatientMedication({
          path: { patient_id: patientId, medication_id: editingMedication.id },
          body: updateData,
        });
        if (response.error) {
          showError('Failed to save medication. Please try again.');
          return;
        }
      } else {
        // Create a brand-new medication.
        const createData: PatientMedicationCreateWeb = {
          ...details,
          ...buildDrugLink(),
          start_date: formatDateToISO(startDate),
          discontinue_date: discontinueDate
            ? formatDateToISO(discontinueDate)
            : null,
          is_change: isChange,
        };
        const response = await createPatientMedication({
          path: { patient_id: patientId },
          body: createData,
        });
        if (response.error) {
          showError('Failed to save medication. Please try again.');
          return;
        }
      }

      onSaved();
      onOpenChange(false);
    } catch (err) {
      console.error('Failed to save medication:', err);
      showError('Failed to save medication. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const isChangeOrNewSelected = isChange || isNew;
  const isLongstandingSelected = isLongstanding;
  const dosageUnit = getDosageUnit(selectedMedication);

  // Screen only a freshly picked FDB drug being ADDED — an edited medication is
  // already assigned (the full panel covers it at signing), and off-market
  // entries have no FDB identity to screen.
  const candidateFdbId =
    !editingMedication &&
    !isOffMarketMode &&
    selectedMedication &&
    !('id' in selectedMedication)
      ? selectedMedication.fdb_dispensable_drug_id
      : null;

  const saveDisabled =
    isSaving ||
    (editingMedication !== null && isMedicationChange && !changeDate) ||
    (isOffMarketMode
      ? !offMarketDescription || !startDate
      : !selectedMedication ||
        !startDate ||
        !frequencyInstructions.trim() ||
        (isNonstandardDosage
          ? !nonstandardDoses.some(d => d.trim())
          : !amount || parseFloat(amount) <= 0));

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className='w-[calc(100vw-2rem)] sm:max-w-2xl'>
        <DialogHeader>
          <DialogTitle>
            {editingMedication
              ? 'Edit Medication'
              : isOffMarketMode
                ? 'Add Off Market / Unlisted Medication'
                : 'Add Medication'}
          </DialogTitle>
          <DialogDescription>
            {editingMedication
              ? 'Choose whether to correct an error or make a medication change.'
              : isOffMarketMode
                ? 'Enter the details for the unlisted medication.'
                : 'Search for a medication and enter the dosage details.'}
          </DialogDescription>
          {isOffMarketMode &&
            (!editingMedication || !editingMedication.is_off_market) && (
              <div className='mt-3 p-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-800'>
                <p>
                  Use only for medications not found in the Medispan database.
                </p>
                <p>
                  These medications will not be included in the clinical
                  interaction checks.
                </p>
              </div>
            )}
        </DialogHeader>

        <div className='space-y-4 py-4'>
          {/* Edit mode selector - only shown when editing */}
          {editingMedication && (
            <div className='p-3 bg-muted/50 rounded-lg space-y-3'>
              <Label className='text-xs text-muted-foreground block'>
                Edit Mode
              </Label>
              <div className='flex gap-1 p-0.5 bg-muted rounded-md w-fit'>
                {(
                  [
                    { value: false, label: 'Error Correction' },
                    { value: true, label: 'Medication Change' },
                  ] as const
                ).map(option => (
                  <button
                    key={String(option.value)}
                    type='button'
                    onClick={() => setIsMedicationChange(option.value)}
                    className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                      isMedicationChange === option.value
                        ? 'bg-background text-foreground font-medium shadow-sm'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              <p className='text-xs text-muted-foreground'>
                {isMedicationChange
                  ? 'Existing medication will be discontinued and a new one created.'
                  : 'Existing medication will be updated in place.'}
              </p>
              {isMedicationChange && (
                <div>
                  <Label className='text-xs text-muted-foreground mb-1 block'>
                    Change Date
                  </Label>
                  <DatePicker
                    date={changeDate}
                    onDateChange={setChangeDate}
                    placeholder='Select change date'
                    datePickerSize='default'
                    minDate={parseISOToLocalDate(editingMedication.start_date)}
                  />
                </div>
              )}
            </div>
          )}

          {/* Medication type selector */}
          <div>
            <Label className='text-xs text-muted-foreground mb-1.5 block'>
              Type
            </Label>
            <div className='flex gap-1 p-0.5 bg-muted rounded-md w-fit'>
              {(
                [
                  { id: 'standard', label: 'Standard' },
                  { id: 'nonstandard', label: 'Nonstandard Dosage' },
                  { id: 'offmarket', label: 'Off Market' },
                ] as const
              ).map(option => {
                const isActive =
                  (option.id === 'offmarket' && isOffMarketMode) ||
                  (option.id === 'nonstandard' &&
                    isNonstandardDosage &&
                    !isOffMarketMode) ||
                  (option.id === 'standard' &&
                    !isNonstandardDosage &&
                    !isOffMarketMode);
                return (
                  <button
                    key={option.id}
                    type='button'
                    onClick={() => {
                      if (option.id === 'standard') {
                        setIsOffMarketMode(false);
                        setIsNonstandardDosage(false);
                      } else if (option.id === 'nonstandard') {
                        setIsOffMarketMode(false);
                        setIsNonstandardDosage(true);
                      } else {
                        setIsOffMarketMode(true);
                        setIsNonstandardDosage(true);
                        if (
                          selectedMedication?.full_name &&
                          !offMarketDescription
                        ) {
                          setOffMarketDescription(selectedMedication.full_name);
                        }
                      }
                    }}
                    className={`px-3 py-1.5 text-xs rounded-md transition-colors ${
                      isActive
                        ? 'bg-background text-foreground font-medium shadow-sm'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Row 1: Start Date | Discontinue Date */}
          <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
            <div>
              <Label className='text-xs text-muted-foreground mb-1 block'>
                Start Date
              </Label>
              <DatePicker
                date={startDate}
                onDateChange={date => {
                  setStartDate(date);
                  if (date && discontinueDate && date > discontinueDate) {
                    setDiscontinueDate(undefined);
                  }
                }}
                placeholder='Select date'
                datePickerSize='default'
              />
            </div>
            <div>
              <Label className='text-xs text-muted-foreground mb-1 block'>
                Discontinue Date
              </Label>
              <div className='flex items-center gap-1'>
                <DatePicker
                  date={discontinueDate}
                  onDateChange={setDiscontinueDate}
                  placeholder='Optional'
                  datePickerSize='default'
                  minDate={startDate}
                />
                {discontinueDate && (
                  <Button
                    type='button'
                    variant='ghost'
                    size='sm'
                    className='h-9 w-9 p-0 text-muted-foreground hover:text-foreground'
                    onClick={() => setDiscontinueDate(undefined)}
                  >
                    <X className='h-4 w-4' />
                  </Button>
                )}
              </div>
            </div>
          </div>

          {isOffMarketMode ? (
            /* Off-market medication form */
            <>
              <div>
                <Label className='text-xs text-muted-foreground mb-1 block'>
                  Drug / Route / Strength / Amount / Form / Frequency / Comments
                </Label>
                <Textarea
                  value={offMarketDescription}
                  onChange={e => setOffMarketDescription(e.target.value)}
                  placeholder='e.g., Aspirin 325mg tablet, take 1 tablet by mouth daily with food'
                  textareaSize='default'
                  rows={3}
                />
              </div>

              <div>
                <Label className='text-xs text-muted-foreground mb-1 block'>
                  Classification
                </Label>
                <Select
                  value={offMarketClassification}
                  onValueChange={setOffMarketClassification}
                >
                  <SelectTrigger>
                    <SelectValue placeholder='Select classification' />
                  </SelectTrigger>
                  <SelectContent>
                    {MEDICATION_CLASSIFICATIONS.map(classification => (
                      <SelectItem
                        key={classification.value}
                        value={classification.value}
                      >
                        {classification.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          ) : (
            /* Standard medication form */
            <>
              <div className='grid grid-cols-12 gap-3 items-end'>
                <div
                  className={isNonstandardDosage ? 'col-span-12' : 'col-span-8'}
                >
                  <Label className='text-xs text-muted-foreground mb-1 block'>
                    Medication
                  </Label>
                  {editingMedication && !isMedicationChange ? (
                    // Error Correction edits metadata only; the drug link is
                    // fixed. Lock the field so the picker can't imply a swap
                    // that save would silently drop — swaps go through
                    // Medication Change.
                    <>
                      <Input
                        value={selectedMedication?.full_name ?? ''}
                        disabled
                        inputSize='default'
                      />
                      <p className='text-xs text-muted-foreground mt-1'>
                        To change the drug, use Medication Change.
                      </p>
                    </>
                  ) : (
                    <MedicationSearch
                      value={selectedMedication}
                      onSelect={setSelectedMedication}
                      onClear={() => setSelectedMedication(null)}
                      onAddOffMarket={handleAddOffMarket}
                      placeholder='Search medications...'
                    />
                  )}
                </div>
                {!isNonstandardDosage && (
                  <div className='col-span-4'>
                    <Label className='text-xs text-muted-foreground mb-1 block'>
                      Amount
                    </Label>
                    <div className='flex items-center gap-2'>
                      <Input
                        type='number'
                        value={amount}
                        onChange={e => setAmount(e.target.value)}
                        placeholder='0'
                        min='0'
                        inputSize='default'
                        className='w-20'
                      />
                      {dosageUnit && (
                        <span className='text-sm text-muted-foreground whitespace-nowrap'>
                          {dosageUnit}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <CandidateScreenAlert
                patientId={patientId}
                fdbDispensableDrugId={candidateFdbId}
              />

              {isNonstandardDosage && (
                <div className='space-y-2'>
                  <Label className='text-xs text-muted-foreground block'>
                    Dose
                  </Label>
                  {nonstandardDoses.map((dose, index) => (
                    <div key={index} className='flex items-center gap-2'>
                      <Input
                        type='text'
                        value={dose}
                        onChange={e => updateDoseRow(index, e.target.value)}
                        placeholder='Enter dose (e.g., 1 tablet in the morning)'
                        inputSize='default'
                        className='flex-1'
                      />
                      {index === nonstandardDoses.length - 1 && (
                        <Button
                          type='button'
                          variant='outline'
                          size='sm'
                          onClick={addDoseRow}
                        >
                          <Plus className='h-4 w-4' />
                        </Button>
                      )}
                      {index > 0 && (
                        <Button
                          type='button'
                          variant='ghost'
                          size='sm'
                          onClick={() => removeDoseRow(index)}
                        >
                          <Trash2 className='h-4 w-4 text-muted-foreground' />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              <div>
                <Label className='text-xs text-muted-foreground mb-1 block'>
                  Frequency / Instructions
                </Label>
                <Textarea
                  value={frequencyInstructions}
                  onChange={e => setFrequencyInstructions(e.target.value)}
                  placeholder='e.g., Take 1 tablet by mouth twice daily with food'
                  textareaSize='default'
                  rows={2}
                />
              </div>
            </>
          )}

          {/* Status checkboxes - shown in all modes */}
          <TooltipProvider>
            <div className='flex items-center gap-6'>
              <div className='flex items-center gap-2'>
                <Checkbox
                  id='longstanding'
                  checked={isLongstanding}
                  onCheckedChange={checked =>
                    setIsLongstanding(checked === true)
                  }
                  disabled={isChangeOrNewSelected}
                />
                <Label
                  htmlFor='longstanding'
                  className={`text-sm cursor-pointer ${isChangeOrNewSelected ? 'text-muted-foreground' : ''}`}
                >
                  Longstanding
                </Label>
              </div>

              <Tooltip>
                <TooltipTrigger asChild>
                  <div className='flex items-center gap-2 cursor-default'>
                    <Checkbox
                      id='change'
                      checked={isChange}
                      onCheckedChange={checked => setIsChange(checked === true)}
                      disabled={isLongstandingSelected}
                    />
                    <Label
                      htmlFor='change'
                      className={`text-sm cursor-pointer ${isLongstandingSelected ? 'text-muted-foreground' : ''}`}
                    >
                      Change
                    </Label>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Medication changed within last 60 days</p>
                </TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <div className='flex items-center gap-2 cursor-default'>
                    <Checkbox
                      id='new'
                      checked={isNew}
                      onCheckedChange={checked => setIsNew(checked === true)}
                      disabled={isLongstandingSelected}
                    />
                    <Label
                      htmlFor='new'
                      className={`text-sm cursor-pointer ${isLongstandingSelected ? 'text-muted-foreground' : ''}`}
                    >
                      New
                    </Label>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Medication added within last 30 days</p>
                </TooltipContent>
              </Tooltip>
            </div>
          </TooltipProvider>
        </div>

        <DialogFooter>
          <Button
            variant='outline'
            onClick={() => handleClose(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saveDisabled}>
            {isSaving && <Loader2 className='h-4 w-4 mr-2 animate-spin' />}
            {editingMedication
              ? isMedicationChange
                ? 'Save as New Medication'
                : 'Save Changes'
              : 'Add Medication'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MedicationFormDialog;
